import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Plus, Check, AlertCircle } from 'lucide-react';
import api from '../services/api';

const AdminCohortManager = () => {
  const [cohorts, setCohorts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [newCohort, setNewCohort] = useState({ code: '', name: '' });
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    fetchCohorts();
  }, []);

  const fetchCohorts = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get('/cohorts/');
      setCohorts(response.data);
    } catch (err) {
      console.error('Failed to fetch cohorts:', err);
      setError('コホート情報の取得に失敗しました。');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateCohort = async () => {
    if (!newCohort.code || !newCohort.name) {
      alert('コードと名称は必須です');
      return;
    }
    setIsCreating(true);
    try {
      await api.post('/cohorts/', {
        ...newCohort,
        is_active: true,
        is_default: false
      });
      setNewCohort({ code: '', name: '' });
      fetchCohorts();
    } catch (err) {
      console.error('Failed to create cohort:', err);
      alert('コホートの作成に失敗しました');
    } finally {
      setIsCreating(false);
    }
  };

  const handleSetDefault = async (cohortId) => {
    if (!window.confirm('この年度をデフォルト（新規登録時の割当先）に設定しますか？')) return;
    try {
      await api.put(`/cohorts/${cohortId}/default`);
      fetchCohorts();
    } catch (err) {
      console.error('Failed to set default cohort:', err);
      alert('デフォルト設定の更新に失敗しました');
    }
  };

  const handleToggleActive = async (cohortId, currentStatus) => {
    try {
      await api.put(`/cohorts/${cohortId}/active?is_active=${!currentStatus}`);
      fetchCohorts();
    } catch (err) {
      console.error('Failed to toggle active status:', err);
      alert('ステータスの更新に失敗しました');
    }
  };

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900">年度（コホート）管理</h2>
          <p className="text-sm text-gray-500">
            ユーザーが所属する年度を管理します。デフォルトに設定された年度は、新規ユーザー登録時に自動的に割り当てられます。
          </p>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-3 mb-8">
        {/* 新規作成フォーム */}
        <div className="space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200 h-fit">
          <h3 className="font-bold text-gray-900">新規年度作成</h3>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">年度コード (例: 2026)</label>
              <input
                type="text"
                value={newCohort.code}
                onChange={(e) => setNewCohort({ ...newCohort, code: e.target.value })}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                placeholder="2026"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">表示名 (例: 2026年度)</label>
              <input
                type="text"
                value={newCohort.name}
                onChange={(e) => setNewCohort({ ...newCohort, name: e.target.value })}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                placeholder="2026年度"
              />
            </div>
            <Button 
              onClick={handleCreateCohort} 
              disabled={isCreating}
              className="w-full bg-blue-600 text-white hover:bg-blue-700 disabled:bg-blue-300"
            >
              <Plus className="w-4 h-4 mr-2" /> 
              {isCreating ? '作成中...' : '追加'}
            </Button>
          </div>
        </div>

        {/* 一覧テーブル */}
        <div className="md:col-span-2">
          {isLoading ? (
            <div className="text-center py-8 text-gray-500">読み込み中...</div>
          ) : error ? (
            <div className="text-center py-8 text-red-600 flex items-center justify-center gap-2">
              <AlertCircle size={18} />
              {error}
            </div>
          ) : (
            <div className="overflow-hidden rounded-lg border border-gray-200">
              <Table>
                <TableHeader className="bg-gray-50">
                  <TableRow>
                    <TableHead className="w-[100px]">コード</TableHead>
                    <TableHead>名称</TableHead>
                    <TableHead className="text-center w-[100px]">デフォルト</TableHead>
                    <TableHead className="text-center w-[100px]">有効</TableHead>
                    <TableHead className="text-right">操作</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cohorts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                        データがありません
                      </TableCell>
                    </TableRow>
                  ) : (
                    cohorts.map((cohort) => (
                      <TableRow key={cohort.id} className="hover:bg-gray-50">
                        <TableCell className="font-mono font-medium">{cohort.code}</TableCell>
                        <TableCell>{cohort.name}</TableCell>
                        <TableCell className="text-center">
                          {cohort.is_default ? (
                            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-100 text-emerald-600">
                              <Check size={14} />
                            </span>
                          ) : (
                            <span className="text-gray-300">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <button
                            onClick={() => handleToggleActive(cohort.id, cohort.is_active)}
                            className={`inline-flex items-center justify-center w-8 h-5 rounded-full transition-colors ${
                              cohort.is_active ? 'bg-blue-600' : 'bg-gray-200'
                            }`}
                          >
                            <span className={`block w-3 h-3 bg-white rounded-full transform transition-transform ${
                              cohort.is_active ? 'translate-x-1.5' : '-translate-x-1.5'
                            }`} />
                          </button>
                        </TableCell>
                        <TableCell className="text-right">
                          {!cohort.is_default && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleSetDefault(cohort.id)}
                              className="text-xs h-8"
                            >
                              デフォルトにする
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default AdminCohortManager;
